import { isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';

import { PictureProfile } from '../../../../../ResizedImage/PictureProfile.csharp';
import { IReactComponentProps } from '../../../../Blocks/Common/IReactComponentProps.csharp';
import { Loader } from '../../../../Components/Loader/Loader';
import { Components } from '../../../../ReactComponents';
import { SetQuantityResult } from '../../../Checkout/Summary/LineItemsApi';
import { ICartSummary } from '../../../FarmasietCart/ICartSummary.csharp';
import { CartDrawerSettingsPageViewModel } from '../../CartDrawer/Page/CartDrawerSettingsPageViewModel.csharp';
import CartItem from '../../Item/Components/CartItem/CartItem';
import { CartSummaryFreeDeliveryInfo } from '../CartSummaryFreeDeliveryInfo/CartSummaryFreeDeliveryInfo';
import { CartSummaryFooter } from './CartSummaryFooter';

export interface CartSummaryProps {
    model: ICartSummary;
    setQuantity: (productCode: string, quantity: number) => Promise<SetQuantityResult>;
    removeFromCart: (productCode: string) => Promise<boolean>;
    pictureProfile: PictureProfile;
    resetLastUpdatedItem?: () => void;
    lastUpdatedItem?: string;
    showFooter?: boolean;
    cartSummaryUrl?: string;
    isEditable: boolean;
    showFreeDeliveryInfoSection?: boolean;
    isLoaderActive: boolean;
    cartDrawerSettingsPageViewModel?: CartDrawerSettingsPageViewModel;
}

export class CartSummary extends React.Component<CartSummaryProps> {
    private readonly ScrollableBox = React.createRef<HTMLDivElement>();

    render() {
        const cart = this.props.model;
        const hasRxItems = cart.items.some((item) => item.isRx);
        const showAdditionalContent = this.props.cartDrawerSettingsPageViewModel
            ? (isMobile()
                  ? cart.items.length <= this.props.cartDrawerSettingsPageViewModel?.maxProductsOnMobile
                  : cart.items.length <= this.props.cartDrawerSettingsPageViewModel?.maxProductsOnDesktop) &&
              this.props.cartDrawerSettingsPageViewModel.additionalContent
            : false;
        const lineItems = this.props.isEditable
            ? cart.items.map((item) => <CartItem key={item.code} {...this.props} item={item} scrollToItem={this.scrollToItem} />)
            : cart.items.map((item) => (
                  <CartItem
                      key={item.code}
                      item={item}
                      scrollToItem={this.scrollToItem}
                      pictureProfile={this.props.pictureProfile}
                      hideItemAvailability
                      showQuantity
                      priceLayout="checkout"
                  />
              ));

        return (
            <>
                <div className={classNames('CartSummary', !this.props.isEditable && 'CartItems--NotEditable', this.props.isEditable && 'CartItems--Editable')}>
                    <div className="CartSummary__ScrollableBox" ref={this.ScrollableBox}>
                        {this.props.showFreeDeliveryInfoSection && cart.shippingInfo && cart.shippingInfo.cheapestMethodInfo && (
                            <CartSummaryFreeDeliveryInfo
                                shippingInfo={cart.shippingInfo}
                                subTotalWithDiscountsIncludingHelfo={cart.subTotalWithDiscountsIncludingHelfo}
                                inlineVersion={false}
                                deliveryPriceCalculationInfo={this.props.model.deliveryPriceCalculationLogic}
                            />
                        )}
                        {cart.items.length > 0 && <ul className="CartSummary__ItemList">{lineItems}</ul>}
                        <ul className="CartSummary__AdditionalContent">
                            {showAdditionalContent &&
                                this.props.cartDrawerSettingsPageViewModel!.additionalContent!.map((component) => (
                                    <li key={component.componentName}>
                                        {React.createElement(Components[component.componentName], {
                                            ...this.buildComponentProps(component)
                                        })}
                                    </li>
                                ))}
                        </ul>
                    </div>
                    {this.props.showFooter !== false && (
                        <CartSummaryFooter
                            hasErrors={cart.hasErrors}
                            cartSummaryUrl={this.props.cartSummaryUrl || ''}
                            subTotalWithDiscounts={cart.subTotalWithDiscounts}
                            shippingInfo={cart.shippingInfo}
                            hasRxItems={hasRxItems}
                        />
                    )}
                    <Loader isLoading={this.props.isLoaderActive} />
                </div>
            </>
        );
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    private buildComponentProps = (componentProps: IReactComponentProps): {} => {
        return { ...componentProps, shippingInfo: this.props.model.shippingInfo };
    };

    private onItemsListScroll = () => {
        this.props.resetLastUpdatedItem && this.props.resetLastUpdatedItem();
        this.ScrollableBox.current && this.ScrollableBox.current.removeEventListener('scroll', this.onItemsListScroll);
    };

    private scrollToItem = (scrollPosition: number) => {
        const itemsListRef = this.ScrollableBox.current;
        this.ScrollableBox.current && this.ScrollableBox.current.removeEventListener('scroll', this.onItemsListScroll);

        if (itemsListRef) {
            itemsListRef.scrollTop = scrollPosition;

            setTimeout(() => {
                itemsListRef.addEventListener('scroll', this.onItemsListScroll);
            }, 200);
        }
    };
}
